import React from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SectionSurface from './SectionSurface';
import {
  DELIVERY_HERO,
  DELIVERY_GUIDING_PRINCIPLES,
  DELIVERY_AGILE_LOOP,
  DELIVERY_SYSTEM_OF_RECORD,
  DELIVERY_BAND_COMPACT,
} from '../../../content/enterpriseMessaging';

/**
 * Compact delivery-model band for home and solution pages.
 * Links to /delivery for the full operating model.
 */
export default function DeliveryBand({
  id = 'delivery',
  compact = false,
  variant = 'elevated',
  decor = 'flow',
}) {
  if (compact) {
    const c = DELIVERY_BAND_COMPACT;
    return (
      <SectionSurface variant={variant} as="section" id={id} decor={decor} bleed="both" flowTop flowBottom overlap className="section">
        <div className="container-x" data-testid="delivery-band-compact">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <h2 className="lg:col-span-4 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">{c.heading}</h2>
            <ol className="lg:col-span-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {c.principles.map((t, i) => (
                <li key={t} className="border-t-2 border-brand-700 pt-4">
                  <span className="text-xs font-bold tracking-widest text-brand-600">{String(i + 1).padStart(2, '0')}</span>
                  <p className="mt-1 text-sm leading-relaxed text-ink-700">{t}</p>
                </li>
              ))}
            </ol>
            <div className="lg:col-span-3 lg:text-right">
              <a href={c.link.href} className="btn-primary">
                {c.link.label}
                <ArrowRightIcon className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </SectionSurface>
    );
  }

  const principles = DELIVERY_GUIDING_PRINCIPLES.slice(0, 3);
  const loop = DELIVERY_AGILE_LOOP.slice(0, 5);

  return (
    <SectionSurface
      variant={variant}
      as="section"
      id={id}
      decor={decor}
      bleed="both"
      flowTop
      flowBottom
      overlap
      className="section"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 lg:items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">{DELIVERY_HERO.eyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              Delivery with rigor, not theater.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-600">
              A nine-stage delivery flow, and{' '}
              {`${
                DELIVERY_SYSTEM_OF_RECORD.title.toLowerCase().includes('azure')
                  ? 'Azure DevOps as the system of record'
                  : 'a single system of record'
              }, so every increment has owners, acceptance criteria, and support before archive.`}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a href="/delivery" className="btn-primary">
                Explore the delivery model
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a href="/contact?service=delivery-model" className="btn-secondary">
                Talk delivery with us
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-8">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              {principles.map((p) => (
                <article key={p} className="border-t-2 border-brand-700 pt-4">
                  <h3 className="text-sm font-semibold text-ink-900 leading-snug">{p}</h3>
                </article>
              ))}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-500">
                Iteration loop
              </p>
              <ol className={`mt-4 grid gap-4 ${compact ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-5'}`}>
                {loop.map((s, i) => (
                  <li key={s.step} className="border-t border-ink-200 pt-3">
                    <span className="text-xs font-bold tracking-widest text-brand-600">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p className="mt-1 text-sm font-semibold text-ink-900">{s.step}</p>
                    {!compact ? (
                      <p className="mt-1 text-xs leading-relaxed text-ink-500">{s.body}</p>
                    ) : null}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </SectionSurface>
  );
}
